import React from "react";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { GeneralButton, GeneralInput } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { usePostData } from "../../../hooks";
import { useToast } from "../../../Services/ToastContext";
import Modal, { ModalProps } from "../../../components/Common/Modal";

interface TemplateNameModalProps extends ModalProps {
  //TODO
  updatedTemplate: any;
}

const TemplateNameModal: React.FC<TemplateNameModalProps> = ({
  updatedTemplate,
  ...props
}) => {
  const methods = useForm();
  const { PatientId } = useParams();

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isPending } = usePostData(
    EndPoints.CreateMedicalHistoryTypes,
  );

  const { showToast } = useToast();

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FieldValues> = (newNames) => {
    mutate(
      {
        ...updatedTemplate,
        ...newNames,
        Name: newNames?.NameEn,
      },
      {
        onSuccess: (data) => {
          props?.onClose && props?.onClose();
          showToast(t("success"), "success");
          queryClient.refetchQueries({
            queryKey: [EndPoints.MedicalHistoryTypes],
            exact: false,
          });
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetMostUsedMedicalHistoryType],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [EndPoints.GetAllPatientMedicalRecords],
            exact: false,
          });
          navigate(
            PatientId
              ? `/patient/${PatientId}/add-medical-record/${data?.Data?.TemplateId}`
              : `/medical-record-templates`,
          );
        },
        onError: (error: any) =>
          showToast(error.message || t("failed"), "error"),
      },
    );
  };

  return (
    <Modal {...props} header={t("enter_new_template_name")}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-3"
        >
          <GeneralInput
            englishOnly
            placeholder={t("template_name_en")}
            name={"NameEn"}
          />
          <GeneralInput
            arabicOnly
            placeholder={t("template_name_ar")}
            name={"NameAr"}
          />
          <div className="ms-auto mt-4 flex w-fit gap-3">
            <GeneralButton
              isLoading={isPending}
              type="submit"
              title={t("save_template")}
            />
            <GeneralButton
              color="lite"
              onClick={props?.onClose}
              title={t("cancel")}
            />
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default TemplateNameModal;
